// Bandeau de consentement Loi 25 (QC) — affiché tant que l'utilisateur
// n'a pas choisi explicitement Accepter ou Refuser. Persistance via
// cookie 1st-party (pas localStorage : règle projet + Loi 25 demande
// que le choix expire après 13 mois max, ce qu'un cookie gère bien).
//
// Au clic : push consent_update à GTM (Google Consent Mode v2) → les
// tags Meta Pixel / GA4 / Google Ads se débloquent (ou restent bloqués).
//
// Exigences Loi 25 :
//  - Refus aussi facile que l'acceptation (boutons de même taille/poids)
//  - Pas de pré-cochage
//  - Lien clair vers la politique de confidentialité
//  - Pas de tracking AVANT le choix (assuré par initGTM avec default=denied)

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { updateConsent } from '@/lib/analytics/gtm';
import {
  CONSENT_REOPEN_EVENT,
  getConsentCookie,
  setConsentCookie,
} from './consentHelpers';

export default function ConsentBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Si déjà choisi : on applique le consent à GTM (au cas où c'est un
    // nouveau onglet/session) et on n'affiche pas le bandeau.
    const existing = getConsentCookie();
    if (existing) {
      updateConsent(existing);
    } else {
      // Pas de choix → on attend un peu avant d'afficher (évite flash au
      // load + laisse le temps à l'utilisateur de voir la page).
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Écoute l'event "reopen" déclenché depuis la page politique de
  // confidentialité (l'user veut modifier son choix).
  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, handler);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, handler);
  }, []);

  const handle = (value: 'granted' | 'denied') => {
    setConsentCookie(value);
    updateConsent(value);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-desc"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto rounded-xl border border-[#D4A574]/30 bg-[#0A0A0A]/95 backdrop-blur-md shadow-2xl shadow-black/50 p-5 md:p-6">
        <div className="flex items-start gap-4">
          <Cookie className="h-6 w-6 text-[#D4A574] shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <h2
              id="consent-title"
              className="font-display text-lg text-[#E8E0D6] mb-1.5"
            >
              On utilise des témoins
            </h2>
            <p
              id="consent-desc"
              className="text-sm text-neutral-400 leading-relaxed"
            >
              On utilise des cookies pour mesurer l'audience du site (Google
              Analytics) et pour personnaliser les publicités qu'on peut te
              montrer sur Facebook ou Instagram. Tu peux refuser sans
              conséquence — le site fonctionne pareil.{' '}
              <Link
                to="/politique-confidentialite"
                className="text-[#D4A574] hover:underline"
              >
                En savoir plus
              </Link>
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
              <Button
                onClick={() => handle('granted')}
                className="bg-[#D4A574] text-[#0A0A0A] hover:bg-[#C9A36B]"
              >
                Accepter
              </Button>
              <Button
                onClick={() => handle('denied')}
                variant="outline"
                className="border-[#D4A574]/40 text-[#E8E0D6] hover:bg-[#1F1F1F]"
              >
                Refuser
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handle('denied')}
            aria-label="Fermer (équivalent à refuser)"
            className="text-neutral-500 hover:text-[#E8E0D6] transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
